import {
  CustomField,
  CustomFieldDataType,
  SubscriptionFormCustomField,
} from './types';

export interface ValidationError {
  dataName: string;
  message: string;
}

/**
 * Validates custom field values before form submission
 * @param fields - Array of configured form fields
 * @param values - Record of field values keyed by displayName (the field name in Echobox)
 * @param fieldDefinitions - Array of field definitions with type info
 * @returns Array of validation errors (empty if all valid)
 */
export function validateCustomFields(
  fields: SubscriptionFormCustomField[],
  values: Record<string, any>,
  fieldDefinitions: CustomField[]
): ValidationError[] {
  const errors: ValidationError[] = [];

  fields.forEach(field => {
    const definition = fieldDefinitions.find(
      def => def.dataName === field.dataName
    );

    if (!definition) {
      return; // Skip if definition not found
    }

    // Values are keyed by displayName (the field name in Echobox)
    const value = values[definition.displayName];

    // Check mandatory fields
    if (field.isMandatory && isEmptyValue(value)) {
      errors.push({
        dataName: field.dataName,
        message:
          field.errorMessage ||
          `${field.displayLabel || definition.displayName} is required`,
      });
      return;
    }

    // Skip validation if value is empty and field is not mandatory
    if (isEmptyValue(value)) {
      return;
    }

    // Type-specific validation
    const typeError = validateFieldType(field, value, definition);
    if (typeError) {
      errors.push(typeError);
    }

    // Constraint validation (enum values)
    const constraintError = validateFieldConstraint(field, value, definition);
    if (constraintError) {
      errors.push(constraintError);
    }
  });

  return errors;
}

/**
 * Check if a value is considered empty
 */
function isEmptyValue(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * Validate field value matches its data type
 */
function validateFieldType(
  field: SubscriptionFormCustomField,
  value: any,
  definition: CustomField
): ValidationError | null {
  const errorMessage =
    field.errorMessage || `Invalid ${definition.displayName}`;

  switch (definition.dataType) {
    case CustomFieldDataType.NUMBER:
      if (typeof value !== 'number' || isNaN(value)) {
        return {
          dataName: field.dataName,
          message: errorMessage,
        };
      }
      break;

    case CustomFieldDataType.DATE:
      // Validate date format (YYYY-MM-DD or similar)
      if (typeof value === 'string') {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(value)) {
          return {
            dataName: field.dataName,
            message: errorMessage,
          };
        }
        // Check if it's a valid date
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          return {
            dataName: field.dataName,
            message: errorMessage,
          };
        }
      } else {
        return {
          dataName: field.dataName,
          message: errorMessage,
        };
      }
      break;

    case CustomFieldDataType.BOOLEAN:
      if (typeof value !== 'boolean') {
        return {
          dataName: field.dataName,
          message: errorMessage,
        };
      }
      break;

    case CustomFieldDataType.MULTI_SELECT:
    case CustomFieldDataType.SET_STRING:
      if (!Array.isArray(value)) {
        return {
          dataName: field.dataName,
          message: errorMessage,
        };
      }
      break;

    // STRING, SINGLE_SELECT, COUNTRY don't need additional type validation
    default:
      break;
  }

  return null;
}

/**
 * Validate field value matches its constraints (e.g., enum values)
 */
function validateFieldConstraint(
  field: SubscriptionFormCustomField,
  value: any,
  definition: CustomField
): ValidationError | null {
  if (!definition.constraint || !definition.constraint.values) {
    return null;
  }

  const allowedValues = definition.constraint.values;
  const errorMessage =
    field.errorMessage ||
    `Invalid value for ${definition.displayName}. Must be one of: ${allowedValues.join(', ')}`;

  switch (definition.dataType) {
    case CustomFieldDataType.SINGLE_SELECT:
      if (!allowedValues.includes(value)) {
        return {
          dataName: field.dataName,
          message: errorMessage,
        };
      }
      break;

    case CustomFieldDataType.MULTI_SELECT:
    case CustomFieldDataType.SET_STRING:
      if (Array.isArray(value)) {
        const invalidValues = value.filter(v => !allowedValues.includes(v));
        if (invalidValues.length > 0) {
          return {
            dataName: field.dataName,
            message: errorMessage,
          };
        }
      }
      break;

    default:
      break;
  }

  return null;
}

/**
 * Get error message for a specific field
 */
export function getFieldError(
  dataName: string,
  errors: ValidationError[]
): string | undefined {
  const error = errors.find(err => err.dataName === dataName);
  return error?.message;
}
